import { useEffect, useMemo } from "react"

import styles from "./AbortModalContent.module.scss"

import { getFragmentData } from "@/main/gql/__generated__"
import { useEvent } from "@/public/event"
import { DocumentType, graphql } from "@/public/gql/__generated__/gql"
import { GetSevenDaysReservableSlotsChunksV2Query } from "@/public/gql/__generated__/graphql"
import {
	formatGraphQLDayToJapaneseMonthDay,
	newDateByGraphQLDayAndHourAndMinute,
} from "@/utils/date"

const recommendedSlotFragment = graphql(/* GraphQL */ `
	fragment recommendedSlot on RecommendedSlot {
		slot {
			startHour
			startMinute
		}
		date
		staff {
			scrapingTargetSiteId
			name
		}
		equipment {
			id
			name
		}
	}
`)

type RecommendedSlot = DocumentType<typeof recommendedSlotFragment>

const formatTimeRange = (
	startHour: number,
	startMinute: number,
	durationMinute: number
): string => {
	const startTime = `${startHour.toString().padStart(2, "0")}:${startMinute.toString().padStart(2, "0")}`

	const endMinutes = startHour * 60 + startMinute + durationMinute
	const endHour = Math.floor(endMinutes / 60)
	const endMinute = endMinutes % 60
	const endTime = `${endHour.toString().padStart(2, "0")}:${endMinute.toString().padStart(2, "0")}`

	return `${startTime} - ${endTime}`
}

type Props = {
	recommendedSlots: GetSevenDaysReservableSlotsChunksV2Query["sevenDaysReservableSlotsChunksV2"]["recommendedSlots"]
	onAbortConfirm: () => void
	onCancel: () => void
	onRecommendedSlotReservation: (
		startTime: Date,
		staffId: string,
		staffName: string,
		// 予約作成 API に送信する設備ID配列
		equipmentIds: string[]
	) => void
	durationMinute: number
}

const AbortModalContent = ({
	recommendedSlots,
	onAbortConfirm,
	onCancel,
	onRecommendedSlotReservation,
	durationMinute,
}: Props) => {
	const event = useEvent()

	const handleRecommendedSlotReservation = (
		selectedRecommendedSlot: RecommendedSlot
	) => {
		if (selectedRecommendedSlot) {
			const startTime = newDateByGraphQLDayAndHourAndMinute(
				selectedRecommendedSlot.date,
				selectedRecommendedSlot.slot.startHour,
				selectedRecommendedSlot.slot.startMinute
			)

			event.clickRecommendedSlotInAbortPopup(
				startTime,
				selectedRecommendedSlot.staff.scrapingTargetSiteId
			)

			onRecommendedSlotReservation(
				startTime,
				selectedRecommendedSlot.staff.scrapingTargetSiteId,
				selectedRecommendedSlot.staff.name,
				// NOTE: needSame のときはそもそも recommendedSlot が返ってこないので考慮不要。
				// needAny のときは equipment が帰ってくるので、使用。
				// needAny が false のときは帰ってこないので [] になる。
				selectedRecommendedSlot.equipment
					? [selectedRecommendedSlot.equipment.id]
					: []
			)
		}
	}

	const recommendedSlotsFragmentData: RecommendedSlot[] = useMemo(
		() =>
			recommendedSlots.slice(0, 4).map((slot) => ({
				...getFragmentData(recommendedSlotFragment, slot),
			})),
		[recommendedSlots]
	)

	useEffect(() => {
		const recommendedSlotsForGA = recommendedSlotsFragmentData.map((slot) => ({
			staffId: slot.staff.scrapingTargetSiteId,
			startTime: newDateByGraphQLDayAndHourAndMinute(
				slot.date,
				slot.slot.startHour,
				slot.slot.startMinute
			),
		}))
		event.openAbortPopup(recommendedSlotsForGA)
	}, [])

	return (
		<div className={styles.modalContainer}>
			<button
				className={styles.closeButton}
				onClick={() => {
					event.closeAbortPopup()
					onCancel()
				}}
			>
				×
			</button>
			<div className={styles.contentWrapper}>
				<div className={styles.mainTitle}>メールの配信を停止しますか？</div>

				<div className={styles.warningDescription}>
					配信停止後は、予約リマインダーやキャンペーン情報などのメールが届かなくなります。
				</div>

				{recommendedSlotsFragmentData.length > 0 && (
					<>
						<div className={styles.divider} />

						<div className={styles.alternativeDescription}>
							代わりに、別の日程でのご予約はいかがですか？
						</div>

						<div className={styles.recommendedSlotsList}>
							{recommendedSlotsFragmentData.map((recommendedSlot, index) => (
								<div
									key={index}
									className={styles.slotItem}
									onClick={() =>
										handleRecommendedSlotReservation(recommendedSlot)
									}
								>
									<div className={styles.slotCard}>
										<div className={styles.dateSection}>
											<div className={styles.dateText}>
												{formatGraphQLDayToJapaneseMonthDay(
													recommendedSlot.date
												)}
											</div>
										</div>

										<div className={styles.timeSection}>
											<div className={styles.timeText}>
												{formatTimeRange(
													recommendedSlot.slot.startHour,
													recommendedSlot.slot.startMinute,
													durationMinute
												)}
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					</>
				)}
			</div>

			<div className={styles.buttonArea}>
				<div className={styles.cancelButtonWrapper}>
					<div className={styles.buttonBase}>
						<div
							className={styles.cancelButton}
							onClick={() => {
								event.closeAbortPopup()
								onCancel()
							}}
						>
							キャンセル
						</div>
					</div>
				</div>

				<div className={styles.confirmButtonWrapper}>
					<div className={styles.buttonBase}>
						<div className={styles.stopButton} onClick={onAbortConfirm}>
							配信を停止する
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default AbortModalContent
